import { HorizontalBar, Bar, Line } from "react-chartjs-2";
import createChartComponentsForDiagramConfigs from "./CaptionTableChartComponent.js";
import {
  addCommasToLabels,
  addPercentageToLabels,
  shortenLabels,
} from "Config/chartjs.global";
import Tables from "Components/Tables/KeyStatistics";
import GenericTable from "Components/Tables/GenericTable";
import Captions from "Captions";
import { sortAgeGroups } from "Lookups";
import config from "Config";
import round from "Utils/round";
import sortFunc from "Utils/sortFunc";
import generateComparisonChartDataset from "../../utils/generateComparisonChartDataset.js";

const definitionOfMESC =
  "Main English speaking countries include Canada, Ireland, New Zealand, South Africa, United Kingdom, England, Scotland, Wales, Northern Ireland and the United States of America. Australia is excluded.";

const DiagramData = {
  "Population Composition": {
    ChartComponent: null,
    TableComponent: Tables["Population Composition"],
    queries: [
      // queries.length > 2
      "TOTAL_POPULATION",
      "TOTAL_POPULATION_STATE",
      "CATEGORY_BORN_IN_AU",
      "CATEGORY_BORN_IN_AU_STATE_WIDE",
      "BIRTHPLACE_NOT_STATED",
      "BIRTHPLACE_NOT_STATED_STATE_WIDE",
      "CATEGORY_INDIGENOUS_STATUS",
      "CATEGORY_INDIGENOUS_STATUS_STATE",
      "CATEGORY_BORN_IN_EN_SPEAKING",
      "CATEGORY_BORN_IN_EN_SPEAKING_STATE",
      "CATEGORY_AGE_AND_GENDER_TOTAL",
      "CATEGORY_BIRTHPLACE_TOP",
      // this is unused:
      // 'CATEGORY_BORN_IN_EN_SPEAKING'
    ],
    captionDataDerive: ({ geo_total_all, state_total_all }) =>
      ({}.tap((data) => {
        data.total = geo_total_all;
        data.total_perc = round(1)(
          geo_total_all / (state_total_all / 100)
        ).toString();
      })),
    footnote: [definitionOfMESC],
    dataPipeline: (domain_data, showFor) => ([
      total_population, // TOTAL_POPULATION
      total_wa, // TOTAL_POPULATION_STATE
      born_in_australia, // CATEGORY_BORN_IN_AU
      born_in_australia_state_wide, // CATEGORY_BORN_IN_AU_STATE_WIDE
      birthplace_not_stated, // BIRTHPLACE_NOT_STATED
      birthplace_not_stated_state_wide, // BIRTHPLACE_NOT_STATED_STATE_WIDE
      aboriginal, // CATEGORY_INDIGENOUS_STATUS
      aboriginal_state_wide, // CATEGORY_INDIGENOUS_STATUS_STATE
      mainly_english_speaking, // CATEGORY_BORN_IN_EN_SPEAKING
      mainly_english_speaking_state_wide, // CATEGORY_BORN_IN_EN_SPEAKING_STATE
      age_and_gender, // CATEGORY_AGE_AND_GENDER_TOTAL
      birthplace, // CATEGORY_BIRTHPLACE_TOP
    ]) =>
      ({
        geo_total_all: total_population.rows[0].number,
        state_total_all: total_wa.rows[0].number,
        females: age_and_gender.rows
          .filter((r) => r["sexp_sex"] === "Female")
          .map((r) => r["number"])[0],
        males: age_and_gender.rows
          .filter((r) => r["sexp_sex"] === "Male")
          .map((r) => r["number"])[0],
        birthplace_top_name:
          birthplace.rows.length > 0
            ? birthplace.rows[0]["bplp_country_of_birth_of_person"]
            : "None",
        birthplace_top_count:
          birthplace.rows.length > 0 ? birthplace.rows[0]["number"] : 0,
      }.chain(
        ({
          geo_total_all,
          state_total_all,
          females,
          males,
          birthplace_top_name,
          birthplace_top_count,
        }) => ({
          tableRows: [
            {
              /*
               * Born in Australia
               */
              id: "Born in Australia",
              geo_total: born_in_australia.rows.length
                ? born_in_australia.rows.filter(
                    (r) => r["born_in_australia"]
                  )[0].number
                : 0,
              state_total: born_in_australia_state_wide.rows.filter(
                (r) => r["born_in_australia"]
              )[0].number,
            },
            {
              /*
               * Aboriginal and Torres Strait Islander
               */
              id: "Aboriginal and Torres Strait Islander (ATSI)",
              geo_total: aboriginal.rows.reduce(
                (sum, row) => row["number"] + sum,
                0
              ),
              state_total: aboriginal_state_wide.rows.reduce(
                (sum, row) => row["number"] + sum,
                0
              ),
            },
            {
              /*
               * Born Overseas
               */
              id: "Overseas born",
              geo_total: born_in_australia.rows.length
                ? born_in_australia.rows.filter(
                    (r) => !r["born_in_australia"]
                  )[0].number
                : 0,
              state_total: born_in_australia_state_wide.rows.filter(
                (r) => !r["born_in_australia"]
              )[0].number,
            },
            {
              /*
               * Not main English speaking (NMES) countries
               */
              id: "Non-Main English speaking (NMES) countries",
              geo_total: mainly_english_speaking.rows.length
                ? mainly_english_speaking.rows.filter(
                    (r) => !r["not_main_english_speaking"]
                  )[0].number
                : 0,
              state_total: mainly_english_speaking_state_wide.rows.filter(
                (r) => !r["not_main_english_speaking"]
              )[0].number,
            },
            {
              /*
               * Main English speaking (MES) countries
               */
              id: "Main English speaking (MES) countries",
              geo_total: mainly_english_speaking.rows.length
                ? mainly_english_speaking.rows.filter(
                    (r) => r["main_english_speaking"]
                  )[0].number
                : 0,
              state_total: mainly_english_speaking_state_wide.rows.filter(
                (r) => r["main_english_speaking"]
              )[0].number,
            },
            {
              /*
               * Birthplace Not Stated
               */
              id: "Not Stated",
              geo_total: birthplace_not_stated.rows[0].number,
              state_total: birthplace_not_stated_state_wide.rows[0].number,
            },
            {
              /*
               * Total Population
               */
              id: "Total Population",
              geo_total: total_population.rows[0].number,
              state_total: total_wa.rows[0].number,
            },
          ].map(
            /*
             * Add percentages
             */
            ({ id, geo_total, state_total }) => ({
              id,
              geo_total,
              geo_pct: (
                Math.round((geo_total / geo_total_all) * 1000) / 10
              ).toLocaleString("en-AU", { minimumFractionDigits: 1 }),
              state_total,
              state_pct: (
                Math.round((state_total / state_total_all) * 1000) / 10
              ).toLocaleString("en-AU", { minimumFractionDigits: 1 }),
            })
          ),
          geo_total_all,
          state_total_all,
          females,
          males,
          birthplace_top_name,
          birthplace_top_count,
        })
      )),
  },
  "Age and Gender": {
    hasComparison: ({ geo_aggregation }, showFor) =>
      showFor === "community" ||
      (showFor === "geo_aggregation" && geo_aggregation !== "ste"),
    comparisonQuery: "CATEGORY_AGE_COMPARISON",
    ChartComponent: HorizontalBar,
    TableComponent: Tables["Age and Gender"] || GenericTable,
    queries: ["CATEGORY_AGE_AND_GENDER"],
    captionDataDerive: (_data) =>
      ({}.tap((data) => {
        data.total_male = _data.datasets[0].data.reduce(
          (total, _set) => total + _set,
          0
        );
        data.total_female = _data.datasets[1].data.reduce(
          (total, _set) => total + _set,
          0
        );
        (data.total = data.total_male + data.total_female),
          (data.prct_male = round(1)(data.total_male / (data.total / 100)));
        data.prct_female = round(1)(data.total_female / (data.total / 100));

        let maxIndex = _data.datasets[0].data.reduce(
          (maxIndex, b, index) =>
            _data.datasets[0].data[maxIndex] +
              _data.datasets[1].data[maxIndex] >
            b + _data.datasets[1].data[index]
              ? maxIndex
              : index,
          0
        );

        data.max_name =
        typeof _data.labels[maxIndex] === "object"
          ? _data.labels[maxIndex].join(" ")
          : _data.labels[maxIndex];

        data.max_count =
        _data.datasets[0].data[maxIndex] + _data.datasets[1].data[maxIndex];

        data.max_perc = round(1)(
          data.max_count /
            ((data.total) / 100)
        );

        data.max_count_state = _data.rows_state.reduce(
          (
            sum,
            { age5p_age_in_five_year_groups: name, number }
          ) => (name.slice(0,3) == data.max_name.slice(0,3) ? sum + number : sum),
          0
        );
        data.total_perc_state = round(1)(
          data.max_count_state / (_data.total_population_state / 100)
        );
        _data.rows_state;
        data.max_name;
        data.max_count_state;
        _data.total_population_state;
        data.total_perc_state;
      })),
    dataPipeline: () => ([
      { rows, fields },
      {
        rows: [{ number: total_population }],
      },
      { rows: rows_state },
      {
        rows: [{ number: total_population_state }],
      },
      {
        rows: [{ number: total_population_na }],
      },
      comparison_data = {},
    ]) => ({
      total_population,
      rows_state,
      total_population_state,
      total_population_na,
      labels: [
        ...new Set(
          rows.map((r) => {
            // // regex to find the year ranges and create labels
            // Below is padding logic but removing since it's probably not needed
            // Also interferes with the max value label logic
            // let found = r["age5p_age_in_five_year_groups"].match(
            //   /^(\d+)(-(\d+))?/
            // );
            // return found[1] && found[3]
            //   ? `${found[1].padStart(2, "0")}-${found[3].padStart(2, "0")}`
            //   : `${found[1]}+`;
            return r["age5p_age_in_five_year_groups"];
          })
        ),
      ],
      datasets: [
        {
          label: "Male",
          backgroundColor: config.primary_chart_color,
          data: rows
            .filter((r) => r["sexp_sex"] == "Male")
            .map((r) => r["number"]),
        },
        {
          label: "Female",
          backgroundColor: config.secondary_chart_color,
          data: rows
            .filter((r) => r["sexp_sex"] == "Female")
            .map((r) => r["number"]),
        },
      ],
      table: {
        rows,
        fields,
        name: "Age and Gender",
        sortFunc: {
          age5p_age_in_five_year_groups: sortAgeGroups(
            "age5p_age_in_five_year_groups"
          ),
        },
      },
      comparison_data: comparison_data.chain((d) => {
        if (Object.keys(d).length === 0) return null;

        const state_rows = d["rows"]
          .filter((r) => r["community"] === config.admin_state_name)
          .sort((a, b) => sortAgeGroups("service_age")(a, b));

        const community_rows = d["rows"]
          .filter((r) => r["community"] !== config.admin_state_name)
          .sort((a, b) => sortAgeGroups("service_age")(a, b));

        const labels = [
          ...new Set(
            community_rows.map((r) => r["service_age"])
          ),
        ];

        const datasets = [
          {
            label: community_rows[0]["community"],
            data: community_rows.map(({ percentage }) => percentage),
            figureValues: community_rows.map(({ number }) => number),
            backgroundColor: "#4372C4",
          },
          {
            label: state_rows[0]["community"],
            data: state_rows.map(({ percentage }) => percentage),
            figureValues: state_rows.map(({ number }) => number),
            backgroundColor: "#ED7D31",
          },
        ];

        return { labels, datasets };
      }),
    }),
    chartOptions: {
      tooltips: { callbacks: { label: addCommasToLabels } },
    },
    comparisonChartOptions: {
      tooltips: { callbacks: { label: addPercentageToLabels } },
      scales: {
        yAxes: [
          {
            ticks: {
              // Include a % sign in the ticks
              callback: (value, index, values) => `${value}%`,
            },
          },
        ],
      },
    },
  },
  "Service Age Groups": {
    hasComparison: ({ geo_aggregation }, showFor) =>
      showFor === "community" ||
      (showFor === "geo_aggregation" && geo_aggregation !== "ste"),
    comparisonQuery: "CATEGORY_SERVICE_AGE_COMPARISON",
    ChartComponent: HorizontalBar,
    TableComponent: Tables["Service Age Groups"] || GenericTable,
    queries: ["CATEGORY_SERVICE_AGE"],
    captionDataDerive: (_data) =>
      ({}.tap((data) => {
        
        let maxIndex = _data.datasets[0].data.reduce(
          (maxIndex, b, index) =>
            _data.datasets[0].data[maxIndex] +
              _data.datasets[1].data[maxIndex] >
            b + _data.datasets[1].data[index]
              ? maxIndex
              : index,
          0
        );
        data.max_name =
          typeof _data.labels[maxIndex] === "object"
            ? _data.labels[maxIndex].join(" ")
            : _data.labels[maxIndex];
        data.max_count =
          _data.datasets[0].data[maxIndex] + _data.datasets[1].data[maxIndex];

       // data.max_count_state = _data.rows_state.reduce(
         // (sum, {service_age: name, number }) =>
        //    name == data.max_name ? sum + number : sum,
        //  0
      //  );
      //  data.total_perc_state = round(1)(
      //    data.max_count_state / (_data.total_population_state / 100)
       // );
        
        data.total_male = _data.datasets[0].data.reduce(
          (total, _set) => total + _set,
          0
        );
        data.total_female = _data.datasets[1].data.reduce(
          (total, _set) => total + _set,
          0
        );
        (data.total = data.total_male + data.total_female),
          (data.prct_male = round(1)(data.total_male / (data.total / 100)));
        data.prct_female = round(1)(data.total_female / (data.total / 100));
        data.total_perc = round(1)(data.max_count /((data.total) / 100));
      })),
    dataPipeline: () => ([{ rows, fields }, comparison_data = {}]) => ({
      labels: [
        ...new Set(
          rows.map((r) => {
            // regex to find the year ranges and create labels
            let found = r["service_age"].match(
              /^(\d+)(-(\d+))?/
            );
            return found[1] && found[3]
              ? `${found[1].padStart(2, "0")}-${found[3].padStart(2, "0")}`
              : `${found[1]}+`;
          })
        ),
      ],
      datasets: [
        {
          label: "Male",
          backgroundColor: config.primary_chart_color,
          data: rows
            .filter((r) => r["sexp_sex"] == "Male")
            .map((r) => r["number"]),
        },
        {
          label: "Female",
          backgroundColor: config.secondary_chart_color,
          data: rows
            .filter((r) => r["sexp_sex"] == "Female")
            .map((r) => r["number"]),
        },
      ],
      table: {
        rows,
        fields,
        name: "Service Age Groups"
      },
      comparison_data: comparison_data.chain((d) => {
        if (Object.keys(d).length === 0) return null;

        const state_rows = d["rows"]
          .filter((r) => r["community"] === config.admin_state_name)
          .sort((a, b) => sortAgeGroups("service_age")(a, b));

        const community_rows = d["rows"]
          .filter((r) => r["community"] !== config.admin_state_name)
          .sort((a, b) => sortAgeGroups("service_age")(a, b));

        const labels = [
          ...new Set(
            community_rows.map((r) => r["service_age"])
          ),
        ];

        const datasets = [
          {
            label: community_rows[0]["community"],
            data: community_rows.map(({ percentage }) => percentage),
            figureValues: community_rows.map(({ number }) => number),
            backgroundColor: "#4372C4",
          },
          {
            label: state_rows[0]["community"],
            data: state_rows.map(({ percentage }) => percentage),
            figureValues: state_rows.map(({ number }) => number),
            backgroundColor: "#ED7D31",
          },
        ];

        return { labels, datasets };
      }),
    }),
    chartOptions: {
      tooltips: { callbacks: { label: addCommasToLabels } },
    },
    comparisonChartOptions: {
      tooltips: { callbacks: { label: addPercentageToLabels } },
      scales: {
        yAxes: [
          {
            ticks: {
              // Include a % sign in the ticks
              callback: (value, index, values) => `${value}%`,
            },
          },
        ],
      },
    },
  },
  "Indigenous Status": {
    hasComparison: ({ geo_aggregation }, showFor) =>
      showFor === "geo_aggregation" && geo_aggregation !== "ste",
    comparisonQuery: "CATEGORY_INDIGENOUS_STATUS_COMPARISON",
    ChartComponent: HorizontalBar,
    TableComponent: Tables["Indigenous Status"] || GenericTable,
    queries: ["CATEGORY_INDIGENOUS_STATUS"],
    captionDataDerive: (_data) =>
      ({}.tap((data) => {
        data.total = _data.datasets[0].data.reduce(
          (total, current) => total + current,
          0
        );
        data.total_perc = round(1)(
          data.total /
            ((_data.total_population - _data.total_population_na) / 100)
        );

        data.total_state = _data.rows_state.reduce(
          (total, current) => total + current,
          0
        );
        data.total_perc_state = round(1)(
          data.total_state / (_data.total_population_state / 100)
        );
      })),
    dataPipeline: () => ([
      { rows, fields },
      {
        rows: [{ number: total_population }],
      },
      { rows: rows_state },
      {
        rows: [{ number: total_population_state }],
      },
      {
        rows: [{ number: total_population_na }],
      },
      comparison_data = {},
    ]) => ({
      labels: rows.map((r) => shortenLabels(r["ingp_indigenous_status"])),
      datasets: [
        {
          label: "Indigenous Peoples",
          data: rows.map((r) => r["number"]),
          backgroundColor: config.primary_chart_color,
        },
      ],
      table: { rows, fields: fields, name: "Indigenous Status" },
      rows_state: rows_state.map((r) => r["number"]),
      total_population,
      total_population_state,
      total_population_na,
      comparison_data: generateComparisonChartDataset(
        comparison_data,
        "ingp_indigenous_status"
      ),
    }),
    chartOptions: {
      tooltips: { callbacks: { label: addCommasToLabels } },
    },
    comparisonChartOptions: {
      tooltips: { callbacks: { label: addPercentageToLabels } },
      scales: {
        yAxes: [
          {
            ticks: {
              // Include a % sign in the ticks
              callback: (value, index, values) => `${value}%`,
            },
          },
        ],
      },
    },
  },
  "Born Overseas": {
    hasComparison: ({ geo_aggregation }, showFor) =>
      showFor === "community" ||
      (showFor === "geo_aggregation" && geo_aggregation !== "ste"),
    comparisonQuery: "CATEGORY_BORN_OVERSEAS_COMPARISON",
    ChartComponent: Bar,
    TableComponent: Tables["Born in Australia"] || GenericTable,
    queries: ["CATEGORY_BORN_IN_AU"],
    captionDataDerive: (_data) =>
      ({}.tap((data) => {
        data.total_au = _data.datasets[0].data.reduce(
          (total, current) => total + current,
          0
        );
        data.total_os = _data.datasets[1].data.reduce(
          (total, current) => total + current,
          0
        );
        data.total_pct_au = round(1)(
          data.total_au /
            ((_data.total_population - _data.total_population_na) / 100)
        );
        data.total_pct_os = round(1)(
          data.total_os /
            ((_data.total_population - _data.total_population_na) / 100)
        );
        data.total_state_os = _data.rows_state
          .filter((r) => !r["born_in_australia"])
          .reduce((total, current) => total + current["number"], 0);
        data.total_pct_state_os = round(1)(
          data.total_state_os / (_data.total_population_state / 100)
        );
      })),
    dataPipeline: () => ([
      { rows, fields },
      {
        rows: [{ number: total_population }],
      },
      { rows: rows_state }, //
      {
        rows: [{ number: total_population_state }],
      },
      {
        rows: [{ number: total_population_na }],
      },
      comparison_data = {},
    ]) => ({
      labels: [""],
      rows_state,
      total_population_state,
      total_population,
      total_population_na,
      comparison_data: comparison_data.chain((d) => {
        if (Object.keys(d).length === 0) return null;

        const labels = ["Born in Australia", "Born Overseas"];

        // In sorting ensuring that objects with true born_in_australia
        // value before false one to match the labels order
        const state_rows = d["rows"]
          .filter((r) => r["community"] === config.admin_state_name)
          .sort((a, b) => (a["born_in_australia"] ? -1 : 1));
        const community_rows = d["rows"]
          .filter((r) => r["community"] !== config.admin_state_name)
          .sort((a, b) => (a["born_in_australia"] ? -1 : 1));

        const datasets = [
          {
            label: community_rows[0]["community"],
            data: community_rows.map(({ percentage }) => percentage),
            figureValues: community_rows.map(({ number }) => number),
            backgroundColor: "#4372C4", // TODO: put colors in config
          },
          {
            label: state_rows[0]["community"],
            data: state_rows.map(({ percentage }) => percentage),
            figureValues: state_rows.map(({ number }) => number),
            backgroundColor: "#ED7D31",
          },
        ];

        return { labels, datasets };
      }),
      datasets: [
        {
          label: "Born in Australia",
          backgroundColor: config.primary_chart_color,
          data: rows
            .filter((r) => r["born_in_australia"])
            .map((r) => r["number"]),
        },
        {
          label: "Born Overseas",
          backgroundColor: config.secondary_chart_color,
          data: rows
            .filter((r) => !r["born_in_australia"])
            .map((r) => r["number"]),
        },
      ],
      table: {
        rows,
        fields,
        name: "Born in Australia",
        formatters: {
          born_in_australia: (cell) =>
            cell === true ? "Born in Australia" : "Born Overseas",
        },
      },
    }),
    chartOptions: {
      scales: {
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
            },
          },
        ],
      },
    },
    comparisonChartOptions: {
      tooltips: { callbacks: { label: addPercentageToLabels } },
      scales: {
        yAxes: [
          {
            ticks: {
              // Include a % sign in the ticks
              callback: (value, index, values) => `${value}%`,
            },
          },
        ],
      },
    },
  },
  "Born in MES and NMES countries": {
    hasComparison: ({ geo_aggregation }, showFor) =>
      showFor === "community" ||
      (showFor === "geo_aggregation" && geo_aggregation !== "ste"),
    comparisonQuery: "CATEGORY_BORN_IN_MES_NNMES_COMPARISON",
    ChartComponent: Bar,
    TableComponent:
      Tables["Born in main English speaking countries"] || GenericTable,
    queries: ["CATEGORY_BORN_IN_EN_SPEAKING"],
    captionDataDerive: (_data) =>
      ({}.tap((data) => {
        data.total_mes = _data.datasets[0].data[0];
        data.total_nmes = _data.datasets[1].data[0];
        // comparison data is only available when community or geo_aggregation are selected
        if (_data.comparison_data) {
          const selection_dataset = _data.comparison_data.datasets[0];
          data.total_perc_mes = selection_dataset.data[0]
          data.total_perc_nmes = selection_dataset.data[1]
        } 
        else {
          // TODO: figure out why this has different values from the SQL query percentages
          // ref: https://github.com/geografiatotal_mes-au/dlgsc-v2/issues/103
          data.total_perc_mes = round(2)(
            data.total_mes /
            ((_data.total_population - _data.total_population_na) / 100)
          );

          data.total_perc_nmes = round(2)(
            data.total_nmes /
            ((_data.total_population - _data.total_population_na) / 100)
          );
        }
        data.total_state_mes = _data.rows_state[1];
        data.total_perc_state_mes = round(1)(
          data.total_state_mes / (_data.total_population_state / 100)
        );

        data.total_state_nmes = _data.rows_state[0];
        data.total_perc_state_nmes = round(1)(
          data.total_state_nmes / (_data.total_population_state / 100)
        );

        _data.rows_state;

      })),
    dataPipeline: () => ([
      { rows, fields },
      {
        rows: [{ number: total_population }],
      },
      { rows: rows_state },
      {
        rows: [{ number: total_population_state }],
      },
      {
        rows: [{ number: total_population_na }],
      },
      comparison_data = {},
    ]) => ({
      labels: [""],
      datasets: [
        {
          label: "Main English speaking",
          backgroundColor: config.primary_chart_color,
          data: rows
            .filter((r) => r["main_english_speaking"])
            .map((r) => r["number"]),
        },
        {
          label: "Non-main English speaking",
          backgroundColor: config.secondary_chart_color,
          data: rows
            .filter((r) => !r["main_english_speaking"])
            .map((r) => r["number"]),
        },
      ],
      rows_state: rows_state
        .map((r) => r["number"]),
      total_population,
      total_population_state,
      total_population_na,
      comparison_data: comparison_data.chain((d) => {
        if (Object.keys(d).length === 0) return null;

        const labels = ["Mainly English Speaking", "Non-main English Speaking"];

        // In sorting ensuring that objects with true main_english_speaking
        // value before false one to match the labels order
        const state_rows = d["rows"]
          .filter((r) => r["community"] === config.admin_state_name)
          .sort((a, b) => (a["main_english_speaking"] ? -1 : 1));
        const community_rows = d["rows"]
          .filter((r) => r["community"] !== config.admin_state_name)
          .sort((a, b) => (a["main_english_speaking"] ? -1 : 1));

        const datasets = [
          {
            label: community_rows[0]["community"],
            data: community_rows.map(({ percentage }) => percentage),
            figureValues: community_rows.map(({ number }) => number),
            backgroundColor: "#4372C4",
          },
          {
            label: state_rows[0]["community"],
            data: state_rows.map(({ percentage }) => percentage),
            figureValues: state_rows.map(({ number }) => number),
            backgroundColor: "#ED7D31",
          },
        ];

        return { labels, datasets };
      }),
      table: {
        rows,
        fields,
        name: "Born in main English speaking countries",
        formatters: {
          main_english_speaking: (cell) =>
            cell === true
              ? "Born in main English speaking countries"
              : "Born in MES and NMES countries",
        },
      },
    }),
    chartOptions: {
      scales: {
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
            },
          },
        ],
      },
    },
    comparisonChartOptions: {
      tooltips: { callbacks: { label: addPercentageToLabels } },
      scales: {
        yAxes: [
          {
            ticks: {
              // Include a % sign in the ticks
              callback: (value, index, values) => `${value}%`,
            },
          },
        ],
      },
    },
    footnote: [definitionOfMESC],
  },
  "Major Birthplaces": {
    ChartComponent: HorizontalBar,
    TableComponent: Tables["Major Birthplaces"] || GenericTable,
    queries: ["CATEGORY_BIRTHPLACE", "CATEGORY_BORN_IN_AU"],
    captionDataDerive: (_data) =>
      ({}.tap((data) => {
        data.number_born_outside_au = _data.born_in_australia
          .filter((i) => !i.born_in_australia)
          .reduce((total, _) => total + _.number, 0);

        [data.first, data.second, data.third] = _data.labels.slice(0, 3);

        // if any of these: data.first, data.second, data.third is an array join them with a space
        data.first = Array.isArray(data.first) ? data.first.join(" ") : data.first;
        data.second = Array.isArray(data.second) ? data.second.join(" ") : data.second;
        data.third = Array.isArray(data.third) ? data.third.join(" ") : data.third;

        data.total_perc = round(1)(
          data.number_born_outside_au /
            ((_data.total_population - _data.total_population_na) / 100)
        );

        data.not_australia_state = _data.born_in_australia_state
          .filter((i) => !i.born_in_australia)
          .reduce((total, _) => total + _.number, 0);

        data.total_perc_state = round(1)(
          data.not_australia_state / (_data.total_population_state / 100)
        );

        data.first_count = _data.datasets[0].data[0];
        data.first_perc =  round(1)(data.first_count/(_data.total_population/100));

        data.second_count = _data.datasets[0].data[1];
        data.second_perc =  round(1)(data.second_count/(_data.total_population/100));

        data.third_count = _data.datasets[0].data[2];
        data.third_perc =  round(1)(data.third_count/(_data.total_population/100));
      })),
    dataPipeline: () => ([
      { rows, fields },
      { rows: born_in_australia },
      {
        rows: [{ number: total_population }],
      },
      { rows: rows_state },
      { rows: born_in_australia_state },
      {
        rows: [{ number: total_population_state }],
      },
      {
        rows: [{ number: total_population_na }],
      },
    ]) => ({
      labels: rows.map((r) =>
        shortenLabels(r["bplp_country_of_birth_of_person"])
      ),
      born_in_australia,
      total_population,
      born_in_australia_state,
      total_population_state,
      rows_state,
      total_population_na,
      datasets: [
        {
          label: "Birthplaces",
          backgroundColor: config.primary_chart_color,
          data: rows.map((r) => r["number"]),
        },
      ],
      table: {
        rows,
        fields,
        name: "Major Birthplaces",
        hide_fields: ["born_in_australia"],
        formatters: {
          born_in_australia: (cell) =>
            cell === true ? "Born in Australia" : "Born Overseas",
          main_english_speaking: (cell) =>
            cell === true ? "Born in main English speaking" : "Born elsewhere",
        },
      },
    }),
    chartOptions: {
      tooltips: { callbacks: { label: addCommasToLabels } },
    },
    height: 600,
  },
  "Year of Arrival": {
    ChartComponent: Line,
    TableComponent: Tables["Year of Arrival"] || GenericTable,
    queries: ["CATEGORY_YR_OF_ARRIVAL"],
    captionDataDerive: (_data) =>
      ({}.tap((data) => {
        let first_arrival_index = _data.datasets[0].data.findIndex(
          (arrivals) => arrivals > 0
        );
        data.first_year = `${_data.labels[first_arrival_index]}`;
        let { amount, index } = _data.datasets[0].data.reduce(
          (current_max, amount, index) =>
            amount > current_max.amount ? { index, amount } : current_max,
          { index: undefined, amount: 0 }
        );
        data.max_amount = amount;
        data.max_year = `${_data.labels[index]}`;
      })),
    dataPipeline: () => ([{ rows, fields }]) => ({
      labels: rows.map((r) => r["numerical_year_of_arrival"]),
      datasets: [
        {
          label: "Number of Arrivals",
          data: rows.map((r) => r["number"]),
          fill: false,
          borderColor: "#39B7BB",
          pointRadius: 3,
          pointBackgroundColor: "#39B7BB",
        },
      ],
      table: {
        rows,
        fields,
        name: "Year of Arrival",
        formatters: {
          numerical_year_of_arrival: (cell) => cell,
        },
      },
    }),
    chartOptions: {
      tooltips: { callbacks: { label: addCommasToLabels } },
    },
  },
  "LGA Distribution": {
    ChartComponent: HorizontalBar,
    TableComponent: Tables["LGA Distribution"] || GenericTable,
    queries: ["CATEGORY_LGA_DISTRIBUTION"],
    captionDataDerive: (_data) =>
      ({}.tap((data) => {
        _data.total_population

        data.total = _data.total_population;

        [data.first, data.second, data.third] = _data.labels.slice(0, 3);

        data.first_count = _data.datasets[0].data[0];
        data.first_perc =  round(1)(data.first_count/(data.total/100));

        data.second_count = _data.datasets[0].data[1];
        data.second_perc =  round(1)(data.second_count/(data.total/100));

        data.third_count = _data.datasets[0].data[2];
        data.third_perc =  round(1)(data.third_count/(data.total/100));
      
      })),
    dataPipeline: (domain_data) => ([
      { rows, fields }, 
      {
        rows: [{ number: total_population }],
      }
    ]) => ({
      total_population,
      labels: rows.map(
        (r) => r[`local_government_areas_${domain_data.year}_boundaries_ur`]
      ),
      datasets: [
        {
          label: "LGA Distribution",
          backgroundColor: config.primary_chart_color,
          data: rows.map((r) => r["number"]),
        },
      ],
      table: { rows, fields, name: "LGA Distribution" },
    }),
    chartOptions: {
      tooltips: { callbacks: { label: addCommasToLabels } },
    },
  },
  Citizenship: {
    hasComparison: ({ geo_aggregation }, showFor) =>
      showFor === "community" ||
      (showFor === "geo_aggregation" && geo_aggregation !== "ste"),
    comparisonQuery: "CATEGORY_CITIZENSHIP_COMPARISON",
    ChartComponent: Bar,
    TableComponent: Tables["Citizenship"] || GenericTable,
    queries: ["CATEGORY_CITIZENSHIP"],
    captionDataDerive: (_data) =>
      ({}.tap((data) => {
        data.total_aussies = _data.datasets[0].data[0];

        data.pct_aussies = round(1)(
          data.total_aussies /
            ((_data.total_population - (_data.total_population_na || 0)) / 100)
        );

        data.total_aussies_state = _data.rows_state["number"];

        data.pct_aussies_state = round(1)(
          data.total_aussies_state / (_data.total_population_state / 100)
        );
      })),
    dataPipeline: () => ([
      { rows, fields },
      {
        rows: [{ number: total_population }],
      },
      { rows: rows_state },
      {
        rows: [{ number: total_population_state }],
      },
      {
        rows: [{ number: total_population_na }],
      },
      comparison_data = {},
    ]) => ({
      labels: rows.map(
        ({ citp_australian_citizenship }) => citp_australian_citizenship
      ),
      datasets: [
        {
          label: "Citizenship",
          data: rows.map(({ number }) => number),
          backgroundColor: config.primary_chart_color,
        },
      ],
      total_population,
      total_population_state,
      total_population_na,
      comparison_data: generateComparisonChartDataset(
        comparison_data,
        "citp_australian_citizenship"
      ),
      rows_state: rows_state.find(
        ({ citp_australian_citizenship: v }) => v == "Australian Citizen"
      ),
      table: { rows, fields, name: "Citizenship" },
    }),
    chartOptions: {
      scales: {
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
            },
          },
        ],
      },
    },
    comparisonChartOptions: {
      tooltips: { callbacks: { label: addPercentageToLabels } },
      scales: {
        yAxes: [
          {
            ticks: {
              // Include a % sign in the ticks
              callback: (value, index, values) => `${value}%`,
            },
          },
        ],
      },
    },
  },
}.tap((diagrams) =>
  Object.keys(Captions["Key Statistics"]).forEach(
    (key) => (diagrams[key].captions = Captions["Key Statistics"][key])
  )
);

export default createChartComponentsForDiagramConfigs(DiagramData);



// WEBPACK FOOTER //
// ./components/Charts/KeyStatistics.js